import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toastSuccess } from '../utils/toasts';
import AdminSectionTitle from '../components/AdminSectionTitle';
import Button from '../components/atoms/Button';
import { parseFriendlyDateAndHour } from '../utils/dates';
import CustomModal from '../components/atoms/CustomModal';
import Switch from '../components/atoms/Switch';
import Input from '../components/atoms/Input';
import {
  useCreateListMutation, useGetListsQuery,
} from '../store/apis/lists';
import { RootState } from '../store';
import { IconPlay2 } from '../components/icons';
import LoadingLottie from '../components/atoms/LoadingLottie';
import RequireAuth from '../store/utils/RequireAuth';
import { ListDTO } from '../models/list';

interface FormData {
  active: boolean;
  name: string;
  date: string;
  description: string;
}

const schema = yup.object().shape({
  active: yup.boolean().required('El estado es requerido'),
  name: yup.string().required('El nombre es requerido'),
  date: yup.string().required('La fecha es requerida'),
  description: yup.string().required('La descripción es requerida'),
});

const EventListsPage = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user: userData } = useSelector(
    (state: RootState) => state.auth,
  );
  const { data: listsUser, isLoading: isLoadingLists } = useGetListsQuery({ user_id: userData?.id || '' });

  const [createList, { isLoading: isCreatingList, isSuccess: isCreateSuccess }] = useCreateListMutation();

  const [lists, setLists] = useState<ListDTO[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      active: true,
      name: '',
      date: '',
      description: '',
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    console.log(errors);
  }, [errors]);

  useEffect(() => {
    if (listsUser) {
      setLists(listsUser || []);
    }
  }, [listsUser]);

  const resetForm = () => {
    reset({
      active: true,
      name: '',
      date: '',
      description: '',
    });
  };

  useEffect(() => {
    if (userData?.id === '') {
      navigate('..');
    }
  }, [userData?.id]);

  useEffect(() => {
    if (isCreateSuccess) {
      setIsModalOpen(false);
      toastSuccess('Lista creada correctamente');
      resetForm();
    }
  }, [isCreateSuccess]);

  const handleSubmitAddList = (data: FormData) => {
    createList({
      name: data.name,
      description: data.description,
      date: data.date,
      user_id: userData?.id || '',
      active: data.active,
    });
  };

  if (isLoadingLists) {
    return <LoadingLottie />;
  }

  return (
    <RequireAuth>
      <div className='flex flex-col gap-8 my-6 min-h-screen'>
        <AdminSectionTitle
          title={'Listas'}
          icon={<IconPlay2 className='w-12 h-12 rotate-180' />}
        />
        <div className='flex flex-col justify-center items-center glass-surface gap-4'>
          <p className='text-text-primary text-center'>¡Creá una nueva lista para gestionar a los invitados!</p>
          <div className='w-fit'><Button variant='primary' onClick={() => { setIsModalOpen(true); resetForm(); }} >Crear nueva lista</Button></div>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {lists?.map((list) => (
            <div key={list.name} className='flex flex-col glass-surface gap-1'>
              <h1 className='text-text-primary text-xl font-semibold'>{list.name}</h1>
              <p className='text-text-primary text-lg font-medium'>{parseFriendlyDateAndHour(list.date, false, true)}</p>
              <p>{list.description}</p>
              <div className='mt-2'>
                <Button variant='secondary' onClick={() => navigate(`${list.id}`)}>Ver lista</Button>
              </div>
            </div>
          ))}
        </div>
        <CustomModal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          hideCancelButton
        >
          <div className='flex flex-col gap-4 min-w-[60vw] md:min-w-[30vw]'>
            <h1>Crear nueva lista</h1>
            <form onSubmit={handleSubmit(handleSubmitAddList)} className='flex flex-col gap-4'>
              <Controller
                control={control}
                name='active'
                render={({ field: { value, onChange, ...field } }) => (
                  <Switch
                    id='active'
                    label='Lista habilitada'
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name='name'
                render={({ field }) => <Input id='name' label='Nombre' labelAbove {...field} />}
              />
              <Controller
                control={control}
                name='date'
                render={({ field: { value, ...field } }) => (
                  <Input
                    id='date'
                    label='Fecha'
                    labelAbove
                    type='date'
                    value={value || ''}
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name='description'
                render={({ field }) => <Input id='description' label='Descripción' labelAbove {...field} />}
              />
              <div className='flex flex-row gap-2'>
                <Button variant='secondary' onClick={() => setIsModalOpen(false)}>Cancelar</Button>
                <Button type='submit' loading={isCreatingList}>Crear</Button>
              </div>
            </form>
          </div>
        </CustomModal >
      </div >
    </RequireAuth>
  );
};

export default EventListsPage;
